import * as React from "react";
import { Modal, cn } from "../../shared/ui/index.ts";

interface PromptOptions {
  title: string;
  message?: string;
  placeholder?: string;
  defaultValue?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Liste de valeurs interdites (ex. labels existants). Comparaison case-insensitive trim. */
  forbidden?: string[];
  forbiddenMessage?: string;
}

interface ConfirmOptions {
  title: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
}

interface DialogCtxValue {
  prompt: (opts: PromptOptions) => Promise<string | null>;
  confirm: (opts: ConfirmOptions) => Promise<boolean>;
}

const DialogCtx = React.createContext<DialogCtxValue | null>(null);

type ActiveState =
  | {
      kind: "prompt";
      opts: PromptOptions;
      resolve: (v: string | null) => void;
    }
  | {
      kind: "confirm";
      opts: ConfirmOptions;
      resolve: (v: boolean) => void;
    }
  | null;

export function PromptDialogProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [state, setState] = React.useState<ActiveState>(null);
  const [draft, setDraft] = React.useState("");
  const [touched, setTouched] = React.useState(false);

  const prompt = React.useCallback(
    (opts: PromptOptions) =>
      new Promise<string | null>((resolve) => {
        setDraft(opts.defaultValue ?? "");
        setTouched(false);
        setState({ kind: "prompt", opts, resolve });
      }),
    [],
  );

  const confirm = React.useCallback(
    (opts: ConfirmOptions) =>
      new Promise<boolean>((resolve) => {
        setState({ kind: "confirm", opts, resolve });
      }),
    [],
  );

  const value = React.useMemo(() => ({ prompt, confirm }), [prompt, confirm]);

  const close = (result: string | boolean | null) => {
    if (state) {
      if (state.kind === "prompt") {
        state.resolve(typeof result === "string" ? result : null);
      } else {
        state.resolve(result === true);
      }
    }
    setState(null);
  };

  const renderPrompt = (
    opts: PromptOptions,
    resolve: (v: string | null) => void,
  ) => {
    const trimmed = draft.trim();
    const forbiddenLower = (opts.forbidden ?? []).map((s) =>
      s.trim().toLowerCase(),
    );
    const isForbidden =
      trimmed.length > 0 &&
      forbiddenLower.includes(trimmed.toLowerCase()) &&
      trimmed.toLowerCase() !== (opts.defaultValue ?? "").trim().toLowerCase();
    const disabled = trimmed.length === 0 || isForbidden;

    const submit = () => {
      if (disabled) return;
      resolve(trimmed);
      setState(null);
    };

    return (
      <Modal
        open
        onClose={() => close(null)}
        title={opts.title}
        subtitle={opts.message}
        width={440}
        footer={
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => close(null)}
              className="h-9 cursor-pointer rounded-md border border-sf-border bg-white px-3.5 text-[13px] font-medium text-sf-text hover:bg-gray-50"
            >
              {opts.cancelLabel ?? "Annuler"}
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={disabled}
              className={cn(
                "h-9 rounded-md border border-sf-primary bg-sf-primary px-3.5 text-[13px] font-semibold text-white shadow-(--sf-shadow-sm)",
                disabled
                  ? "cursor-not-allowed opacity-50"
                  : "cursor-pointer hover:opacity-90",
              )}
            >
              {opts.confirmLabel ?? "Valider"}
            </button>
          </div>
        }
      >
        <div className="flex flex-col gap-2">
          <input
            autoFocus
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
              setTouched(true);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                submit();
              }
            }}
            placeholder={opts.placeholder}
            className="w-full rounded-md border border-sf-border bg-white px-3 py-2 text-[14px] text-sf-text outline-none focus:border-sf-primary focus:shadow-[0_0_0_3px_color-mix(in_srgb,var(--sf-primary)_18%,transparent)]"
          />
          {touched && isForbidden && (
            <p className="text-[12px] text-sf-danger">
              {opts.forbiddenMessage ?? "Cette valeur est déjà utilisée."}
            </p>
          )}
        </div>
      </Modal>
    );
  };

  const renderConfirm = (
    opts: ConfirmOptions,
    resolve: (v: boolean) => void,
  ) => {
    const accept = () => {
      resolve(true);
      setState(null);
    };
    return (
      <Modal
        open
        onClose={() => close(false)}
        title={opts.title}
        width={420}
        footer={
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => close(false)}
              className="h-9 cursor-pointer rounded-md border border-sf-border bg-white px-3.5 text-[13px] font-medium text-sf-text hover:bg-gray-50"
            >
              {opts.cancelLabel ?? "Annuler"}
            </button>
            <button
              type="button"
              onClick={accept}
              className={cn(
                "h-9 cursor-pointer rounded-md border px-3.5 text-[13px] font-semibold shadow-(--sf-shadow-sm)",
                opts.danger
                  ? "border-sf-danger bg-sf-danger text-white hover:opacity-90"
                  : "border-sf-primary bg-sf-primary text-white hover:opacity-90",
              )}
            >
              {opts.confirmLabel ?? "Confirmer"}
            </button>
          </div>
        }
      >
        {opts.message ? (
          <p className="text-[13.5px] leading-relaxed text-sf-text-secondary">
            {opts.message}
          </p>
        ) : null}
      </Modal>
    );
  };

  return (
    <DialogCtx.Provider value={value}>
      {children}
      {state?.kind === "prompt" && renderPrompt(state.opts, state.resolve)}
      {state?.kind === "confirm" && renderConfirm(state.opts, state.resolve)}
    </DialogCtx.Provider>
  );
}

export function usePromptDialog(): DialogCtxValue {
  const ctx = React.useContext(DialogCtx);
  if (!ctx) {
    throw new Error(
      "usePromptDialog must be used within a PromptDialogProvider",
    );
  }
  return ctx;
}
